import { useEffect, useState } from "react";
import { GrDialog, GrDialogProps, GrIcon, GrProgressCircle, GrStack, GrTypography } from "@jnpmedi/grebob";
import { useQueryClient } from "@tanstack/react-query";
import { NotFoundView } from "@newComponents/NotFoundView";
import { MvnMessage } from "@newLibs/utils";
import { useBoolean, UseBoolean } from "@newHooks/common/useBoolean";
import { useFolderContext } from "@newHooks/context/folder/folder.context";
import { notify } from "@newHooks/context/snackbar/notify";
import { useMoveFolderMutation } from "@newHooks/mutation/folder/moveFolder.mutation";
import { useMoveFolderFileMutation } from "@newHooks/mutation/folderFile/moveFolderFile.mutation";
import { useBrowseFoldersQuery } from "@newHooks/query/folder/browseFolders.query";
import { FolderKey } from "@newHooks/query/folder/key";
import { FolderFileTreeRow } from "./FolderFileTreeRow";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { Organization } from "@maven-rest/auth/Organization.dto";
import { BrowseEntrySummary, BrowseEntryType, FolderType } from "@maven-rest/docs/Folder.dto";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";
interface MoveResourceDialogProps {
  open: UseBoolean;
  organization: Organization;
  targetResource?: BrowseEntrySummary;
  baseFolderType: FolderType;
  size?: GrDialogProps["size"];
}

export function MoveResourceDialog({ open, organization, targetResource, baseFolderType, size = "small" }: MoveResourceDialogProps) {
  const queryClient = useQueryClient();
  const { folderIds: currentFolderIds } = useFolderContext();

  const [browseFoldersEntries, setBrowseFoldersEntries] = useState<BrowseEntrySummary[]>([]);
  const [selectedFolderItems, setSelectedFolderItems] = useState<Array<BrowseEntrySummary>>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<bigint | null>(null);

  const openCheckMoveResourceDialog = useBoolean(false);

  const isConfirmDisable = (() => {
    if (currentFolderIds.length === 0 && selectedFolderId === null) return true;

    const isEqualSelectedFolder = compareBigintSafe(currentFolderIds[currentFolderIds.length - 1], selectedFolderId);
    const isMoveProposalToRoot = targetResource?.type === BrowseEntryType.Proposal && selectedFolderId === null;

    return isEqualSelectedFolder || isMoveProposalToRoot;
  })();

  const { isInitialLoading: isBrowseFoldersInitialLoading } = useBrowseFoldersQuery(
    {
      organizationId: organization.id,
      folderType: baseFolderType,
    },
    {
      queryKey: [FolderKey.BrowseFoldersMoveResource],
      enabled: open.value,
      onSuccess: (data) => {
        if (data.entries) {
          setBrowseFoldersEntries(data.entries);
        }
      },
    }
  );

  const { mutate: moveFolderRequest, isLoading: isMoveFolderLoading } = useMoveFolderMutation(
    { folderId: targetResource!.resource.id },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });
        queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFoldersMoveResource] });

        openCheckMoveResourceDialog.onFalse();
        open.onFalse();

        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_ITEM_MOVE_SUCC), "SUCC");
      },
      onError: (error) => notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_MOVE_FAIL, { message: error.responseMessage }), "ERROR"),
    }
  );

  const { mutate: moveFolderFileRequest, isLoading: isMoveFolderFileLoading } = useMoveFolderFileMutation(
    { fileId: targetResource!.resource.id },
    {
      onSuccess: () => {
        openCheckMoveResourceDialog.onFalse();
        open.onFalse();

        queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });

        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_ITEM_MOVE_SUCC), "SUCC");
      },
      onError: (error) => notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_MOVE_FAIL, { message: error.responseMessage }), "ERROR"),
    }
  );

  const handleMoveResource = () => {
    if (targetResource?.type === BrowseEntryType.Folder) {
      moveFolderRequest({
        folderId: targetResource!.resource.id,
        destinationFolderId: selectedFolderId,
      });
    } else {
      moveFolderFileRequest({
        fileId: targetResource!.resource.id,
        destinationFolderId: selectedFolderId,
      });
    }
    handleCloseMoveResourceDialog();
  };

  const handleCloseMoveResourceDialog = () => {
    resetSelectedFolderItems();
    open.onFalse();
  };

  const resetSelectedFolderItems = () => {
    setSelectedFolderId(null);
    setSelectedFolderItems([]);
  };

  useEffect(() => {
    resetSelectedFolderItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selected = selectedFolderItems.length > 0 ? selectedFolderItems[selectedFolderItems.length - 1].resource.id : null;

    setSelectedFolderId(selected);
  }, [selectedFolderItems]);

  return (
    <GrDialog
      title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_MOVE_DIALOG_HEADER)}
      open={open.value}
      confirmButton={{
        children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_OK_NOUN),
        disabled: isConfirmDisable || isMoveFolderLoading || isMoveFolderFileLoading,
      }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
      onConfirm={openCheckMoveResourceDialog.onTrue}
      onClose={handleCloseMoveResourceDialog}
      size={size}
    >
      <GrStack
        flexDirection="column"
        alignItems={isBrowseFoldersInitialLoading ? "center" : undefined}
        justifyContent={isBrowseFoldersInitialLoading ? "center" : ""}
        height="600px"
      >
        {isBrowseFoldersInitialLoading && <GrProgressCircle hasBackgroundColor value={80} variant="indeterminate" />}
        {!isBrowseFoldersInitialLoading && (
          <>
            <GrStack flexDirection="column" gap={2}>
              <GrStack justifyContent="flex-start" padding="0px 0px 8px">
                {!selectedFolderItems.length ? (
                  <GrTypography type="Content.Caption02" color="grey050">
                    Select a folder
                  </GrTypography>
                ) : (
                  <GrStack gap={2}>
                    {selectedFolderItems.map((item, idx) => {
                      const isLastElement = idx === selectedFolderItems.length - 1;
                      return (
                        <GrStack key={String(item.resource.id)} gap={2} alignItems="center">
                          <GrTypography type={isLastElement ? "Content.Caption01" : "Content.Caption02"} color={"grey080"}>
                            {item.resource.name}
                          </GrTypography>
                          {!isLastElement && <GrIcon type="chevron-right" color="grey050" size={12} />}
                        </GrStack>
                      );
                    })}
                  </GrStack>
                )}
              </GrStack>
              {!browseFoldersEntries?.length && <NotFoundView />}
              {!!browseFoldersEntries?.length && (
                <GrStack flexDirection="column" minHeight="32px" gap={4}>
                  {browseFoldersEntries.map((ent) => (
                    <FolderFileTreeRow
                      key={String(ent.resource.id)}
                      depth={0}
                      organizationId={organization.id}
                      folderFileResource={ent}
                      selectedFolderItems={selectedFolderItems}
                      targetResourceId={targetResource?.resource.id}
                      folderType={baseFolderType}
                      parentFolderItems={[ent]}
                      onFolderItemSelect={setSelectedFolderItems}
                    />
                  ))}
                </GrStack>
              )}
            </GrStack>

            {/* 현재 선택된 경로 초기화를 위한 컴포넌트 */}
            <div style={{ height: "100%", boxSizing: "border-box", width: "100%" }} onClick={resetSelectedFolderItems} />
          </>
        )}
      </GrStack>

      {openCheckMoveResourceDialog.value && (
        <GrDialog
          size="xs"
          title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_MOVE_DIALOG_HEADER)}
          open={openCheckMoveResourceDialog.value}
          onClose={openCheckMoveResourceDialog.onFalse}
          onConfirm={handleMoveResource}
          confirmButton={{
            children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_OK_NOUN),
            isLoading: isMoveFolderLoading || isMoveFolderFileLoading,
            disabled: isConfirmDisable,
          }}
          cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
        >
          <GrTypography>{MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_MOVE_DIALOG_CONFIRM)}</GrTypography>
        </GrDialog>
      )}
    </GrDialog>
  );
}
